/**
 * <park-field> + <park-notice> — form primitives (epic #138, issue #147).
 * Isomorphic: pure render fns shared by the ds/*.astro SSR wrappers and the
 * custom elements (see docs/design-system-rebuild-plan.md). Emit the live
 * `.bk-field`/`.bk-hint`/`.bk-notice` classes lifted from varaa.astro — CSS in
 * wc/css/park-field.css.
 *
 * <park-field>  a labelled control row.
 *   Attributes: label, hint, inline (bool → .bk-field--inline).
 *   Slot = the control (<input>/<select>) — kept as author markup so its
 *          id/name/options stay exactly what page scripts query (JS-coupled).
 *
 * <park-notice>  a status / error message line.
 *   Attributes: variant ("error" → .bk-notice--error).
 *   Slot = the message text.
 *   NOTE: when a page script owns the live text (e.g. varaa's #bk-notice), keep
 *   the id on the inner element the script targets; <park-notice> uses
 *   observedAttributes=[] so it never re-renders over script-set content.
 *
 * @dsSpec booking-flow-ux#FR-18 — the booking form controls a visitor fills to reserve a slot.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const FIELD_TAG = "park-field";
const NOTICE_TAG = "park-notice";

export interface ParkFieldProps {
	label?: string;
	hint?: string;
	inline?: boolean;
}

/** Pure render: (props, control HTML) → inner HTML string. No DOM access. */
export function renderParkField(
	props: ParkFieldProps,
	control: string,
): string {
	const cls = props.inline ? "bk-field bk-field--inline" : "bk-field";
	const labelHtml = props.label ? html`<span>${esc(props.label)}</span>` : "";
	const hintHtml = props.hint
		? html`<small class="bk-hint">${esc(props.hint)}</small>`
		: "";
	return html`<label class="${cls}">${labelHtml}${control}${hintHtml}</label>`;
}

export interface ParkNoticeProps {
	variant?: string;
}

/** Pure render: (props, message HTML) → inner HTML string. No DOM access. */
export function renderParkNotice(
	props: ParkNoticeProps,
	message: string,
): string {
	const cls =
		props.variant === "error" ? "bk-notice bk-notice--error" : "bk-notice";
	return html`<p class="${cls}" role="status" aria-live="polite">${message}</p>`;
}

class ParkField extends ParkElement {
	static get observedAttributes(): string[] {
		return [];
	}

	protected render(): string {
		return renderParkField(
			{
				label: this.attr("label"),
				hint: this.attr("hint"),
				inline: this.boolAttr("inline"),
			},
			this.slotHtml,
		);
	}
}

class ParkNotice extends ParkElement {
	static get observedAttributes(): string[] {
		return [];
	}

	protected render(): string {
		return renderParkNotice({ variant: this.attr("variant") }, this.slotHtml);
	}
}

defineElement(FIELD_TAG, ParkField);
defineElement(NOTICE_TAG, ParkNotice);

export { ParkField, ParkNotice };
